import graphqlClient from "@/GQLClient";
import type { SummarizedNews } from "@/graphql";
import {
  Delete_Type,
  DeleteNewsDocument,
  type EntityPagerFilter,
  GetExternalNewsDocument,
  GetInternalNewsOnMyInstitutionDocument,
  GetSummarizedExternalNewsDocument,
  GetSummarizedInternalNewsDocument,
  Module_Type,
  type NewsInput,
  type NewsUnion,
  SaveNewsDocument,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { IPagerResult } from "@/interfaces";
import type { IdType } from "@/types";

const getNews = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedNews>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedInternalNewsDocument,
    variables: {
      filter,
    },
  });

  if (response.data.institutions?.getMyInstitution.news) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.news;
    return {
      edges: edges.map((item) => item.node as SummarizedNews),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getNewsDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetInternalNewsOnMyInstitutionDocument,
    variables: {
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.news) {
    const { edges } = response.data.institutions.getMyInstitution.news;
    return edges.map((item) => item.node as NewsUnion).at(0);
  }

  throw new SentryGraphQLError();
};

const getExternalNews = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedNews>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedExternalNewsDocument,
    variables: {
      type: Module_Type.News,
      filter,
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.externalContent;
    return {
      edges: edges.map((item) => item.node as SummarizedNews),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getExternalNewsDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetExternalNewsDocument,
    variables: {
      type: Module_Type.News,
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges } =
      response.data.institutions.getMyInstitution.externalContent;
    return edges.map((item) => item.node as NewsUnion).at(0);
  }

  throw new SentryGraphQLError();
};

const save = async (news: NewsInput) => {
  const response = await graphqlClient.mutate({
    mutation: SaveNewsDocument,
    variables: {
      news,
    },
  });

  if (response.data?.news?.save?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

const deleteItem = async (id: string, type: Delete_Type) => {
  const response = await graphqlClient.mutate({
    mutation: DeleteNewsDocument,
    variables: {
      id,
      type,
    },
  });

  if (response.data?.news?.delete?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

export default {
  getNews,
  getNewsDetail,
  getExternalNews,
  getExternalNewsDetail,
  deleteItem,
  save,
};
